import React, { useContext } from 'react';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { CollapseFilterWrapper } from '../../../components/sidebar-filters/CollapseFilterWrapper';
import { ProgramFilterSelect } from '../../../components/sidebar-filters/filter-select/ProgramFilterSelect';
import { DateRangePresetFilterSelect } from '../../../components/sidebar-filters/filter-select/DateRangePresetFilterSelect';
import { EventDurationPresetFilterSelect } from '../../../components/sidebar-filters/filter-select/EventDurationPresetFilterSelect';
import {
    IEventPerformanceSidebarFilters,
    IEventPerformanceSidebarFiltersContext,
    EventPerformanceSidebarFiltersContext,
} from './EventPerformanceSidebarFiltersContext';
import { NominationPresetFilterSelect } from '../../../components/sidebar-filters/filter-select/HasNominationFilterSelect';

export const EventPerformanceFilterKeys = {
    // Filters
    date_range_preset: 'date_range_preset',
    event_duration_preset: 'event_duration_preset',
    program: 'program_name',
    nomination_preset: 'nomination_preset',
} as const;

export type TEventPerformanceFilterKey = (typeof EventPerformanceFilterKeys)[keyof typeof EventPerformanceFilterKeys];

export const EventPerformanceFilterLabels = {
    [EventPerformanceFilterKeys.date_range_preset]: 'Date Range',
    [EventPerformanceFilterKeys.event_duration_preset]: 'Event Duration',
    [EventPerformanceFilterKeys.program]: 'Program',
    [EventPerformanceFilterKeys.nomination_preset]: 'Total Nomination',
} as const;

interface IEventPerformanceSidebarFiltersProps {
    onFilterChange: (filters: IEventPerformanceSidebarFilters) => void;
}

export const EventPerformanceSidebarFilters: React.FC<IEventPerformanceSidebarFiltersProps> = ({ onFilterChange }) => {
    const { filters, handleFilterChange } = useContext<IEventPerformanceSidebarFiltersContext>(EventPerformanceSidebarFiltersContext);
    const {
        isLoading: isFiltersLoading,
        dateRangePresetOptions,
        eventDurationPresetOptions,
        programOptions,
        nominationPresetOptions,
    } = useSidebarFilterOptionsContext();

    const handleEventPerformanceFilterChange = (key: string, value: unknown) => {
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">
                <DateRangePresetFilterSelect
                    height={220}
                    label={EventPerformanceFilterLabels[EventPerformanceFilterKeys.date_range_preset]}
                    value={filters[EventPerformanceFilterKeys.date_range_preset] || null}
                    onChange={value => handleEventPerformanceFilterChange(EventPerformanceFilterKeys.date_range_preset, value)}
                    options={dateRangePresetOptions}
                />
                <NominationPresetFilterSelect
                    label={EventPerformanceFilterLabels[EventPerformanceFilterKeys.nomination_preset]}
                    value={filters[EventPerformanceFilterKeys.nomination_preset] || null}
                    onChange={value => handleEventPerformanceFilterChange(EventPerformanceFilterKeys.nomination_preset, value)}
                    options={nominationPresetOptions}
                />
                <EventDurationPresetFilterSelect
                    height={120}
                    label={EventPerformanceFilterLabels[EventPerformanceFilterKeys.event_duration_preset]}
                    value={filters[EventPerformanceFilterKeys.event_duration_preset] || null}
                    onChange={value => handleEventPerformanceFilterChange(EventPerformanceFilterKeys.event_duration_preset, value)}
                    options={eventDurationPresetOptions}
                />
                <ProgramFilterSelect
                    label={EventPerformanceFilterLabels[EventPerformanceFilterKeys.program]}
                    value={filters[EventPerformanceFilterKeys.program] || []}
                    onChange={value => handleEventPerformanceFilterChange(EventPerformanceFilterKeys.program, value)}
                    options={programOptions.filter(item => item.value !== 'null')} // Remove "No programs" option from selection
                    isLoading={isFiltersLoading}
                />
            </CollapseFilterWrapper>
        </div>
    );
};
